import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  message,
  Spin,
  Space,
  Slider,
  Image,
  Tag,
  Popconfirm,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined, CameraOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import React, { useState, useRef } from 'react';
import { tradesApi, strategyApi } from '@tradermind/shared/api';
import type { TradeImageType } from '@tradermind/shared/types';
import { createTradeSchema, updateTradeSchema, type UpdateTradeFormData } from '@tradermind/shared/validators';

interface LocationState {
  checklist_id?: number;
  checklistId?: number;
  quality_score?: number;
  qualityScore?: number;
  symbol?: string;
  direction?: 'long' | 'short';
}

export default function TradeFormPage() {
  const { t } = useTranslation(['trade', 'strategy']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  // 获取用户策略列表
  const { data: strategiesData } = useQuery({
    queryKey: ['strategies'],
    queryFn: () => strategyApi.getStrategies({ is_active: true, limit: 100 }),
  });
  const strategies = strategiesData?.data?.items || [];

  // 从检查清单页面传递的数据（兼容 snake_case / camelCase）；同时兼容 query params（防止刷新导致 state 丢失）
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const rawChecklistState = React.useMemo(() => {
    const s = ((location.state as LocationState) || {}) as LocationState;
    const qpChecklistId = searchParams.get('checklist_id') ?? searchParams.get('checklistId') ?? undefined;
    const qpQualityScore = searchParams.get('quality_score') ?? searchParams.get('qualityScore') ?? undefined;
    const qpSymbol = searchParams.get('symbol') ?? undefined;
    const qpDirection = (searchParams.get('direction') as 'long' | 'short' | null) ?? undefined;

    return {
      ...s,
      checklist_id: s.checklist_id ?? (qpChecklistId as any),
      checklistId: s.checklistId ?? (qpChecklistId as any),
      quality_score: s.quality_score ?? (qpQualityScore as any),
      qualityScore: s.qualityScore ?? (qpQualityScore as any),
      symbol: s.symbol ?? qpSymbol,
      direction: s.direction ?? qpDirection,
    };
  }, [location.state, searchParams]);
  const checklistState = React.useMemo(
    () => ({
      ...rawChecklistState,
      checklist_id: rawChecklistState.checklist_id ?? rawChecklistState.checklistId,
      quality_score: rawChecklistState.quality_score ?? rawChecklistState.qualityScore,
    }),
    [rawChecklistState]
  );

  const checklistIdFromState = React.useMemo(() => {
    const v = checklistState.checklist_id;
    if (v === null || v === undefined || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }, [checklistState.checklist_id]);

  const qualityScoreFromState = React.useMemo(() => {
    const v = checklistState.quality_score;
    if (v === null || v === undefined || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }, [checklistState.quality_score]);

  const { data: tradeData, isLoading: isLoadingTrade } = useQuery({
    queryKey: ['trade', id],
    queryFn: () => tradesApi.getTradeById(Number(id)),
    enabled: isEdit,
  });

  const trade = tradeData?.data;

  // Keep a stable default open_time for create mode; otherwise the form will reset on each render.
  const createOpenTimeRef = React.useRef<string>(new Date().toISOString());

  const defaultFormValues = React.useMemo<UpdateTradeFormData>(() => {
    if (trade) {
      return {
        ...trade,
        open_time: trade.open_time,
        close_time: trade.close_time || undefined,
      } as unknown as UpdateTradeFormData;
    }

    return {
      symbol: checklistState.symbol || '',
      direction: checklistState.direction || 'long',
      entry_price: undefined,
      quantity: undefined,
      leverage: 1,
      open_time: createOpenTimeRef.current,
      checklist_id: checklistIdFromState,
      quality_score: qualityScoreFromState,
    } as unknown as UpdateTradeFormData;
  }, [trade, checklistState.symbol, checklistState.direction, checklistIdFromState, qualityScoreFromState]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    register,
  } = useForm<UpdateTradeFormData>({
    resolver: zodResolver(isEdit ? updateTradeSchema : createTradeSchema),
    defaultValues: defaultFormValues,
  });

  // Initialize / re-initialize form only when the source data changes (not on every render)
  React.useEffect(() => {
    if (isEdit) {
      if (trade) {
        reset(defaultFormValues);
      }
      return;
    }

    // Create mode: when navigating from checklist, location.key changes and checklistState may carry defaults.
    reset(defaultFormValues);

    // Ensure hidden fields are written into RHF state even if the DOM input doesn't update.
    if (checklistIdFromState !== undefined) {
      setValue('checklist_id', checklistIdFromState, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
    }
    if (qualityScoreFromState !== undefined) {
      setValue('quality_score', qualityScoreFromState, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
    }
  }, [isEdit, trade, location.key, reset, defaultFormValues, checklistIdFromState, qualityScoreFromState, setValue]);

  const [uploadImageType, setUploadImageType] = useState<TradeImageType>('other');
  const [uploading, setUploading] = useState(false);
  const formFileInputRef = useRef<HTMLInputElement>(null);

  const imageTypeLabels: Record<TradeImageType, string> = {
    entry: t('image_type.entry'),
    exit: t('image_type.exit'),
    analysis: t('image_type.analysis'),
    other: t('image_type.other'),
  };

  const imageTypeColors: Record<TradeImageType, string> = {
    entry: 'green',
    exit: 'red',
    analysis: 'blue',
    other: 'default',
  };

  const handleFormUploadImages = async (files: FileList | null) => {
    if (!files || files.length === 0 || !id) return;
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => formData.append('files[]', file));
      formData.append('image_type', uploadImageType);
      await tradesApi.uploadTradeImages(Number(id), formData);
      message.success(t('form_page.upload_success'));
      queryClient.invalidateQueries({ queryKey: ['trade', id] });
    } catch {
      message.error(t('form_page.upload_failed'));
    } finally {
      setUploading(false);
      if (formFileInputRef.current) formFileInputRef.current.value = '';
    }
  };

  const deleteImageMutation = useMutation({
    mutationFn: ({ tradeId, imageId }: { tradeId: number; imageId: number }) =>
      tradesApi.deleteTradeImage(tradeId, imageId),
    onSuccess: () => {
      message.success(t('form_page.image_deleted'));
      queryClient.invalidateQueries({ queryKey: ['trade', id] });
    },
    onError: () => {
      message.error(t('form_page.image_delete_failed'));
    },
  });

  const createMutation = useMutation({
    mutationFn: tradesApi.createTrade,
    onSuccess: (result) => {
      message.success(t('form_page.create_success'));
      queryClient.invalidateQueries({ queryKey: ['trades'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      const newTradeId = result?.data?.id;
      navigate(newTradeId ? `/trades/${newTradeId}` : '/trades');
    },
    onError: (error: any) => {
      const errorMessage = error?.error?.message || t('form_page.create_failed');
      message.error(errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateTradeFormData }) =>
      tradesApi.updateTrade(id, data),
    onSuccess: () => {
      message.success(t('form_page.update_success'));
      queryClient.invalidateQueries({ queryKey: ['trades'] });
      queryClient.invalidateQueries({ queryKey: ['trade', id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      navigate(`/trades/${id}`);
    },
    onError: () => {
      message.error(t('form_page.update_failed'));
    },
  });

  const onSubmit = (data: UpdateTradeFormData) => {
    if (isEdit) {
      updateMutation.mutate({ id: Number(id), data });
    } else {
      // 新建时，必填字段已通过表单验证
      const createData = {
        symbol: data.symbol!,
        direction: data.direction!,
        entry_price: data.entry_price!,
        quantity: data.quantity!,
        open_time: data.open_time!,
        exit_price: data.exit_price,
        leverage: data.leverage,
        profit_loss: data.profit_loss,
        commission: data.commission,
        swap: data.swap,
        close_time: data.close_time,
        strategy: data.strategy,
        pre_trade_emotion: data.pre_trade_emotion,
        tags: data.tags,
        notes: data.notes,
        checklist_id: data.checklist_id ?? checklistIdFromState,
        quality_score: data.quality_score ?? qualityScoreFromState,
        risk_amount: data.risk_amount,
      };
      createMutation.mutate(createData);
    }
  };

  if (isEdit && isLoadingTrade) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            {t('form_page.back')}
          </Button>
          <h2 style={{ margin: 0 }}>{isEdit ? t('form_page.edit_trade') : t('form_page.new_trade')}</h2>
        </Space>
      </div>

      <Form layout="vertical">
        <input type="hidden" {...register('checklist_id', { valueAsNumber: true })} />
        <input type="hidden" {...register('quality_score', { valueAsNumber: true })} />
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Card title={t('form_page.basic_info')}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('form_page.symbol')}
                    validateStatus={errors.symbol ? 'error' : ''}
                    help={errors.symbol?.message}
                    required
                  >
                    <Controller
                      name="symbol"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} placeholder={t('form_page.symbol_placeholder')} />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('form_page.direction')}
                    validateStatus={errors.direction ? 'error' : ''}
                    help={errors.direction?.message}
                    required
                  >
                    <Controller
                      name="direction"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={[
                            { value: 'long', label: t('form_page.long') },
                            { value: 'short', label: t('form_page.short') },
                          ]}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={t('form_page.entry_price')}
                    validateStatus={errors.entry_price ? 'error' : ''}
                    help={errors.entry_price?.message}
                    required
                  >
                    <Controller
                      name="entry_price"
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          style={{ width: '100%' }}
                          placeholder="0.0000"
                          precision={4}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label={t('form_page.exit_price')}>
                    <Controller
                      name="exit_price"
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          style={{ width: '100%' }}
                          placeholder="0.0000"
                          precision={4}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={t('form_page.quantity')}
                    validateStatus={errors.quantity ? 'error' : ''}
                    help={errors.quantity?.message}
                    required
                  >
                    <Controller
                      name="quantity"
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          style={{ width: '100%' }}
                          placeholder="0"
                          precision={4}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item label={t('form_page.leverage')}>
                    <Controller
                      name="leverage"
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          style={{ width: '100%' }}
                          min={1}
                          max={500}
                          addonAfter="x"
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label={t('form_page.pnl')}>
                    <Controller
                      name="profit_loss"
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          style={{ width: '100%' }}
                          placeholder="0.00"
                          precision={2}
                          addonBefore="$"
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label={t('form_page.commission')}>
                    <Controller
                      name="commission"
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          style={{ width: '100%' }}
                          placeholder="0.00"
                          precision={2}
                          addonBefore="$"
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={t('risk_amount')}
                    validateStatus={errors.risk_amount ? 'error' : ''}
                    help={errors.risk_amount?.message}
                    tooltip={t('risk_amount_hint')}
                  >
                    <Controller
                      name="risk_amount"
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          style={{ width: '100%' }}
                          placeholder={t('risk_amount_placeholder')}
                          precision={2}
                          min={0}
                          addonBefore="$"
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('form_page.open_time')}
                    validateStatus={errors.open_time ? 'error' : ''}
                    help={errors.open_time?.message}
                    required
                  >
                    <Controller
                      name="open_time"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) => field.onChange(date?.toISOString())}
                          showTime
                          style={{ width: '100%' }}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label={t('form_page.close_time')}>
                    <Controller
                      name="close_time"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) => field.onChange(date?.toISOString())}
                          showTime
                          style={{ width: '100%' }}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label={t('form_page.strategy')}>
                <Controller
                  name="strategy"
                  control={control}
                  render={({ field }) => (
                    strategies.length > 0 ? (
                      <Select
                        {...field}
                        placeholder={t('strategy:select_strategy')}
                        allowClear
                        options={[
                          { value: '', label: t('strategy:no_strategy') },
                          ...strategies.map((s) => ({
                            value: s.name,
                            label: s.name,
                          })),
                        ]}
                      />
                    ) : (
                      <Input {...field} placeholder={t('form_page.strategy_placeholder')} />
                    )
                  )}
                />
              </Form.Item>

              <Form.Item label={t('form_page.notes')}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <Input.TextArea {...field} rows={4} placeholder={t('form_page.notes_placeholder')} />
                  )}
                />
              </Form.Item>
            </Card>

            {/* 交易截图（仅编辑模式） */}
            {isEdit && (
              <Card
                title={
                  <Space>
                    <CameraOutlined />
                    {t('form_page.trade_images')}
                  </Space>
                }
                style={{ marginTop: 24 }}
                extra={
                  <Space>
                    <Select
                      value={uploadImageType}
                      onChange={setUploadImageType}
                      size="small"
                      style={{ width: 90 }}
                      options={[
                        { value: 'entry', label: imageTypeLabels.entry },
                        { value: 'exit', label: imageTypeLabels.exit },
                        { value: 'analysis', label: imageTypeLabels.analysis },
                        { value: 'other', label: imageTypeLabels.other },
                      ]}
                    />
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      loading={uploading}
                      onClick={() => formFileInputRef.current?.click()}
                    >
                      {t('form_page.upload_image')}
                    </Button>
                    <input
                      ref={formFileInputRef}
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFormUploadImages(e.target.files)}
                    />
                  </Space>
                }
              >
                {trade?.images && trade.images.length > 0 ? (
                  <Image.PreviewGroup>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {trade.images.map((img) => (
                        <div key={img.id} style={{ position: 'relative', textAlign: 'center' }}>
                          <Image
                            src={img.file_url}
                            alt={img.caption || img.original_filename}
                            width={120}
                            height={90}
                            style={{ objectFit: 'cover', borderRadius: 8 }}
                          />
                          <div style={{ marginTop: 4 }}>
                            <Tag color={imageTypeColors[img.image_type as TradeImageType]}>
                              {imageTypeLabels[img.image_type as TradeImageType]}
                            </Tag>
                            <Popconfirm
                              title={t('form_page.delete_image_confirm')}
                              onConfirm={() => deleteImageMutation.mutate({ tradeId: Number(id), imageId: img.id })}
                              okText={t('common:confirm')}
                              cancelText={t('common:cancel')}
                            >
                              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                            </Popconfirm>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Image.PreviewGroup>
                ) : (
                  <div style={{ textAlign: 'center', padding: '16px 0', color: '#999' }}>
                    {t('form_page.no_images')}
                  </div>
                )}
              </Card>
            )}
          </Col>

          <Col xs={24} lg={8}>
            <Card title={t('form_page.emotion_record')}>
              <Form.Item label={t('form_page.pre_emotion')}>
                <Controller
                  name="pre_trade_emotion"
                  control={control}
                  render={({ field }) => (
                    <Slider
                      {...field}
                      min={1}
                      max={10}
                      marks={{ 1: t('form_page.emotion_bad'), 5: '5', 10: t('form_page.emotion_good') }}
                    />
                  )}
                />
              </Form.Item>
              {isEdit && trade?.status === 'closed' && (
                <Form.Item label={t('form_page.post_emotion')}>
                  <Controller
                    name="post_trade_emotion"
                    control={control}
                    render={({ field }) => (
                      <Slider
                        {...field}
                        min={1}
                        max={10}
                        marks={{ 1: t('form_page.emotion_bad'), 5: '5', 10: t('form_page.emotion_good') }}
                      />
                    )}
                  />
                </Form.Item>
              )}
            </Card>

            <Card title={t('form_page.actions')} style={{ marginTop: 24 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  block
                  loading={createMutation.isPending || updateMutation.isPending}
                  onClick={handleSubmit(onSubmit, (errors) => {
                    console.error('Form validation errors:', errors);
                    const firstError = Object.values(errors)[0];
                    if (firstError?.message) {
                      message.error(String(firstError.message));
                    }
                  })}
                >
                  {isEdit ? t('form_page.save') : t('form_page.create')}
                </Button>
                <Button block onClick={() => navigate(-1)}>
                  {t('common:cancel')}
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
}
